import React from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Collapse,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";

import { fmtDate, fmtDateTime, frequencyLabels } from "./recurringInvoiceUtils";

export default function RecurringInvoiceList({
    entries,
    expandedLogs,
    customerLookup,
    onToggleLogs,
    onEdit,
    onDelete,
}) {
    return (
        <Stack spacing={2}>
            {entries.map((entry) => {
                const customerLabel = customerLookup.get(entry.customerId) || entry.customerName || "Unbekannter Kunde";
                const nextRun = fmtDate(entry.nextRun);
                const lastRun = fmtDate(entry.lastRun);
                return (
                    <Card key={entry.id} variant="outlined">
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                                <Box>
                                    <Typography variant="h6">{entry.title || "Ohne Titel"}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Kunde: {customerLabel}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Nächste Ausführung: {nextRun}
                                    </Typography>
                                    {entry.lastInvoiceNumber && (
                                        <Typography variant="body2" color="text.secondary">
                                            Letzte Rechnung: {entry.lastInvoiceNumber} ({lastRun})
                                        </Typography>
                                    )}
                                    {entry.lastPayLink && (
                                        <Typography variant="body2" color="text.secondary">
                                            Letzter Pay-Link:{" "}
                                            <a href={entry.lastPayLink} target="_blank" rel="noreferrer">
                                                öffnen
                                            </a>
                                        </Typography>
                                    )}
                                </Box>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Chip
                                        label={frequencyLabels[entry.frequency] || entry.frequency}
                                        color="primary"
                                        variant="outlined"
                                    />
                                    {entry.autoSendMail && <Chip label="Auto-Mail" color="success" variant="outlined" />}
                                    {entry.includePayLink && <Chip label="Pay-Link" color="secondary" variant="outlined" />}
                                </Stack>
                            </Stack>
                        </CardContent>
                        <CardActions sx={{ justifyContent: "space-between" }}>
                            <Stack direction="row" spacing={1}>
                                <Button startIcon={<HistoryRoundedIcon />} onClick={() => onToggleLogs(entry.id)}>
                                    Versand-Log
                                </Button>
                                <Button startIcon={<EditRoundedIcon />} onClick={() => onEdit(entry)}>
                                    Bearbeiten
                                </Button>
                            </Stack>
                            <Tooltip title="Löschen">
                                <IconButton color="error" onClick={() => onDelete(entry.id)}>
                                    <DeleteRoundedIcon />
                                </IconButton>
                            </Tooltip>
                        </CardActions>
                        <Collapse in={expandedLogs === entry.id} timeout="auto" unmountOnExit>
                            <Divider />
                            <Box sx={{ px: 2, pb: 2 }}>
                                {entry.logs && entry.logs.length > 0 ? (
                                    <List dense>
                                        {entry.logs.map((log, idx) => (
                                            <ListItem key={log.id || `${entry.id}-${idx}`} alignItems="flex-start">
                                                <ListItemText
                                                    primary={`${fmtDate(log.executionDate)} • ${log.status || "STATUS"}`}
                                                    secondary={
                                                        <>
                                                            <Typography component="span" variant="body2" display="block">
                                                                Rechnung: {log.invoiceNumber || "–"}
                                                            </Typography>
                                                            <Typography component="span" variant="body2" display="block">
                                                                Erstellt am: {fmtDateTime(log.createdAt)}
                                                            </Typography>
                                                            <Typography component="span" variant="body2" display="block">
                                                                Mail: {log.emailSent ? "versendet" : "nicht versendet"} | Pay-Link:{" "}
                                                                {log.payLinkCreated ? "erstellt" : "nicht erstellt"}
                                                            </Typography>
                                                            {log.payLink && (
                                                                <Typography component="span" variant="body2" display="block">
                                                                    Link:{" "}
                                                                    <a href={log.payLink} target="_blank" rel="noreferrer">
                                                                        öffnen
                                                                    </a>
                                                                </Typography>
                                                            )}
                                                            {log.message && (
                                                                <Typography
                                                                    component="span"
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    display="block"
                                                                >
                                                                    Hinweis: {log.message}
                                                                </Typography>
                                                            )}
                                                        </>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        Keine Ausführungen vorhanden.
                                    </Typography>
                                )}
                            </Box>
                        </Collapse>
                    </Card>
                );
            })}
            {!entries.length && <Alert severity="info">Noch keine wiederkehrenden Rechnungen angelegt.</Alert>}
        </Stack>
    );
}
